'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import CTAButton from '../ui/CTAButton';
import { useStaggeredAnimateIn } from '@/lib/useAnimateIn';
import { trackEvent } from '@/lib/analytics';

export default function FormSuccessView() {
  const pathname = usePathname();
  const { getItemStyle } = useStaggeredAnimateIn({
    delay: 100,
    staggerDelay: 80,
    autoStart: true
  });

  // Prevent any focus/blinking cursor on component mount
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Blur any currently focused element
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        
        // Blur body if it has focus
        if (document.activeElement === document.body) {
          document.body.blur();
        }
        
        // Also blur any links/buttons in the success view
        const successView = document.querySelector('.view-form-success');
        if (successView) {
          const links = successView.querySelectorAll('a, button');
          links.forEach((el) => {
            if (document.activeElement === el) {
              (el as HTMLElement).blur();
            }
          });
        }
      });
    });
    
    // Remove focus from text elements that might get it
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Prevent focus on text elements (h1, p) and wrapper divs
      if (target.tagName === 'H1' || target.tagName === 'P' || target.classList.contains('view-form-success')) {
        target.blur();
      }
    };
    
    document.addEventListener('focusin', handleFocus);
    
    return () => {
      document.removeEventListener('focusin', handleFocus);
    };
  }, []);

  return (
    <div className='view-form-success view-4 flex flex-col items-center justify-center h-full text-center'>
      <h1 
        className="heading-3 mb-8" 
        style={{ ...getItemStyle(0), outline: 'none', userSelect: 'none', caretColor: 'transparent' }}
        tabIndex={-1}
        contentEditable={false}
        onFocus={(e) => e.target.blur()}
      >
        Thank you for reaching out!
      </h1>
      <p 
        className="body-default mb-12" 
        style={{ ...getItemStyle(1), outline: 'none', userSelect: 'none', caretColor: 'transparent' }}
        tabIndex={-1}
        contentEditable={false}
        onFocus={(e) => e.target.blur()}
      >
        You&rsquo;ll be hearing from our team soon.
      </p>
      <div 
        style={{ ...getItemStyle(2), outline: 'none', caretColor: 'transparent' }}
        tabIndex={-1}
        onFocus={(e) => e.currentTarget.blur()}
      >
        <CTAButton 
          href="/about"
          onClick={() => {
            trackEvent('ai_click', {
              click_text: 'Get in touch - More about us',
              page_path: pathname
            });
          }}
        >
          More about us
        </CTAButton>
      </div>
    </div>
  );
}
